import UserAvatar from "../ui/userAvatar";
import Send from "../../assets/svg/Send.svg";
import useCreateTaskMutation, {
  Comments,
  Task,
} from "@/api/mutation/useTaskCreateMutation";
import { useUser } from "@/hooks/useUser";
import calculateTimeDifference from "../shared/TimeDifferenceCalculate";
import { Button } from "../ui/button";
import { useFormik } from "formik";
import { createCommentTaskSchema } from "@backend/src/schemas/taskSchema";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import useCreateTaskCommentMutation, {
  Comment,
} from "@/api/mutation/useTaskAddCommentMutation";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import useUpdateTaskCommentMutation from "@/api/mutation/useTaskUpdateCommentMutation";
import useRemoveTaskCommentMutation from "@/api/mutation/useTaskRemoveCommentMutation";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import { errorConfig } from "@/lib/toastError";
import { generateUniqueTaskName } from "../shared/taskNameGenrater";
import useAllTaskQuery from "@/api/query/useAllTaskQuery";
import useProjectQuery, { AssignedUsers } from "@/api/query/useProjectQuery";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import ErrorMessage from "../common/ErrorMessage";
import Dialog from "../common/Dialog";
import TrashCan from "../../assets/svg/TrashCan.svg";

type Props = {
  task: Task | undefined;
  refetch: () => void;
  allowed: boolean;
  setTaskId: (id: string) => void;
  setTask: (task: Task) => void;
  projectId?: string;
};

function TaskComment(props: Props) {
  const { task, refetch } = props;
  const currantUser = useUser();
  const [commentId, setCommentId] = useState<string | null>();
  const [showAllShow, setShowAllShow] = useState<boolean>(false);

  const createTaskCommentMutation = useCreateTaskCommentMutation(task?.taskId);
  const updateTaskCommentMutation = useUpdateTaskCommentMutation(
    commentId ?? undefined
  );
  const { t } = useTranslation();
  const taskCreateMutation = useCreateTaskMutation(
    props.projectId,
    props.task?.taskId ? props.task?.taskId : ""
  );
  const projectDetailQuery = useProjectDetail(props.task?.projectId);
  const removeTaskCommentMutation = useRemoveTaskCommentMutation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRefUpdate = useRef<HTMLTextAreaElement | null>(null);

  const commentFormik = useFormik<z.infer<typeof createCommentTaskSchema>>({
    initialValues: {
      commentText: "",
      mantionEmail: [], // Initialize mantionEmail array
    },
    validationSchema: toFormikValidationSchema(createCommentTaskSchema),
    onSubmit: async (values) => {
      let currentTaskId = props.task?.taskId;
      // If no task exists, create one first
      if (!currentTaskId) {
        try {
          const project = projects?.data?.data?.data.find(
            (p) => p.projectId === props.projectId
          );
          const projectStartDate = project?.startDate
            ? new Date(project.startDate)
            : new Date();

          const data = {
            taskName: generateUniqueTaskName(
              allTask.data?.data.data ?? [],
              project?.projectName ?? ""
            ),
            taskDescription: "",
            startDate:
              projectStartDate >= new Date() ? projectStartDate : new Date(),
            duration: 1.0,
          };
          const res = await taskCreateMutation.mutateAsync(data);
          currentTaskId = res.data.data.taskId;
          props.setTaskId(currentTaskId);
          props.setTask(res.data.data);
          toast.success(t("task_created_success"));
        } catch (error: any) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
          return; // Stop submission if task creation failed
        }
      }

      createTaskCommentMutation.mutate(
        { ...values, taskId: currentTaskId! }, // Use the resolved task ID
        {
          onSuccess(data) {
            refetch();
            commentFormik.resetForm();
            toast.success(data.data.message);
          },
          onError(error: any) {
            toast.error(
              error.response?.data.message == "Unauthorized"
                ? t(
                    "You are not authorized to edit tasks which are not assigned to you"
                  )
                : errorKeyFormate(error.response?.data.message + ""),
              errorConfig
            );
          },
        }
      );
    },
  });

  const updateCommentFormik = useFormik<
    z.infer<typeof createCommentTaskSchema>
  >({
    initialValues: {
      commentText: "",
      mantionEmail: [], // Initialize mantionEmail array for update form
    },
    validationSchema: toFormikValidationSchema(createCommentTaskSchema),
    onSubmit: (values) => {
      updateTaskCommentMutation.mutate(values, {
        onSuccess(data) {
          refetch();
          updateCommentFormik.resetForm();
          toast.success(data.data.message);
          setCommentId(null);
        },
        onError(error: any) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                  "You are not authorized to edit tasks which are not assigned to you"
                )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
        },
      });
    },
  });

  const handleEdit = (comment: Comment) => {
    setCommentId(comment.commentId);
    updateCommentFormik.setFieldValue("commentText", comment.commentText);
    // Populate mantionEmail when editing

    const initialMentions = resolveMentionsToEmails(
      comment.commentText
    );
    updateCommentFormik.setFieldValue("mantionEmail", initialMentions);
  };

  const handleBlur = () => {
    // Only submit if the text has actually changed to avoid unnecessary API calls
    const originalComment = task?.comments.find(
      (c) => c.commentId === commentId
    );
    if (
      originalComment?.commentText !== updateCommentFormik.values.commentText
    ) {
      updateCommentFormik.submitForm();
    } else {
      setCommentId(null); // Close edit mode if no change
    }
  };

  const [removeCommnet, setRemoveComment] = useState<Comment>();
  const handleRemove = (comment: Comment) => {
    removeTaskCommentMutation.mutate(comment.commentId, {
      onSuccess(data) {
        refetch();
        toast.success(data.data.message);
        setCommentId(null); // Exit edit mode if it was active
      },
      onError(error: any) {
        toast.error(
          error.response?.data.message == "Unauthorized"
            ? t(
                "You are not authorized to edit tasks which are not assigned to you"
              )
            : errorKeyFormate(error.response?.data.message + ""),
          errorConfig
        );
      },
    });
    setRemoveComment(undefined); // Close dialog
  };

  function isDateSevenDaysOld(inputDate: Date): boolean {
    const currentDate: Date = new Date();
    const sevenDaysAgo: Date = new Date(currentDate);
    sevenDaysAgo.setDate(currentDate.getDate() - 7);
    return new Date(inputDate) <= sevenDaysAgo;
  }

  const visibleComments = showAllShow
    ? props.task?.comments ?? []
    : (props.task?.comments ?? []).filter(
        (comment) => !isDateSevenDaysOld(new Date(comment.createdAt))
      );

  const allowed = (comment: Comment) => {
    return comment.commentByUserId === currantUser.user?.userId;
  };

  const scrollDemoRef = useRef<HTMLDivElement | null>(null);
  // Scrolls to bottom on initial load and when comments change
  useEffect(() => {
    if (scrollDemoRef.current) {
      scrollDemoRef.current.scrollTop = scrollDemoRef.current.scrollHeight;
    }
  }, [props.task?.comments]); // Dependency on comments ensures it scrolls when new comments load

  const allTask = useAllTaskQuery(props.projectId, false);
  const [isMentioning, setIsMentioning] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<AssignedUsers[]>();
  const projects = useProjectQuery();
  const resolveMentionsToEmails = (
    text: string,
  ): string[] => {
    const mentionRegex = /@([\w\s._%+-]+)/g;
    const mentionedEmails = new Set<string>();
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {

      const mentionStart = match.index;

      let bestMatchUser = null;
      let bestMatchLength = 0;

      if (projectDetailQuery.data?.data.data.assignedUsers) {
        for (const assignedUser of projectDetailQuery.data.data.data
          .assignedUsers) {
          const fullName = `${assignedUser.user.firstName ?? ""} ${
            assignedUser.user.lastName ?? ""
          }`.trim();
          const email = assignedUser.user.email || "";

          const possibleMentions = [fullName, email];

          for (const possible of possibleMentions) {
            if (!possible) continue;

            // Check if text starting after @ matches this possible user
            const substr = text.substring(
              mentionStart + 1,
              mentionStart + 1 + possible.length
            );

            if (
              substr.toLowerCase() === possible.toLowerCase() &&
              possible.length > bestMatchLength
            ) {
              bestMatchUser = assignedUser;
              bestMatchLength = possible.length;
            }
          }
        }
      }

      if (bestMatchUser) {
        // Push the highlight
        mentionedEmails.add(bestMatchUser.user.email)
      }
    }
    console.log( Array.from(mentionedEmails));
    
    return Array.from(mentionedEmails);
  };

  const getMentionQuery = (
    text: string,
    cursorPosition: number
  ): string | null => {
    const substring = text.slice(0, cursorPosition);
    const mentionRegex = /@([\w\s._%+-]*)$/;
    const match = substring.match(mentionRegex);
    return match ? match[1] : null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const assignedUsers =
      projectDetailQuery.data?.data?.data?.assignedUsers || [];

    commentFormik.setFieldValue("commentText", value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    const query = getMentionQuery(value, cursorPosition);

    if (query !== null) {
      setIsMentioning(true);

      const matches = assignedUsers.filter((u) => {
        const fullName = `${u.user.firstName ?? ""} ${u.user.lastName ?? ""}`
          .trim()
          .toLowerCase();
        const email = u.user.email.toLowerCase();
        return (
          fullName.startsWith(query.toLowerCase()) ||
          email.startsWith(query.toLowerCase())
        );
      });

      setFilteredUsers(matches);
    } else {
      setIsMentioning(false);
      setFilteredUsers([]);
    }

    const resolvedEmails = resolveMentionsToEmails(value);
    commentFormik.setFieldValue("mantionEmail", resolvedEmails);
  };

  const handleChangeUpdate = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const assignedUsers =
      projectDetailQuery.data?.data?.data?.assignedUsers || [];

    updateCommentFormik.setFieldValue("commentText", value);

    if (textareaRefUpdate.current) {
      textareaRefUpdate.current.style.height = "auto";
      textareaRefUpdate.current.style.height = `${textareaRefUpdate.current.scrollHeight}px`;
    }

    const query = getMentionQuery(value, cursorPosition);

    if (query !== null) {
      setIsMentioning(true);

      const matches = assignedUsers.filter((u) => {
        const fullName = `${u.user.firstName ?? ""} ${u.user.lastName ?? ""}`
          .trim()
          .toLowerCase();
        const email = u.user.email.toLowerCase();
        return (
          fullName.startsWith(query.toLowerCase()) ||
          email.startsWith(query.toLowerCase())
        );
      });

      setFilteredUsers(matches);
    } else {
      setIsMentioning(false);
      setFilteredUsers([]);
    }

    const resolvedEmails = resolveMentionsToEmails(value);
    updateCommentFormik.setFieldValue("mantionEmail", resolvedEmails);
  };

  const handleSelectUser = (user: AssignedUsers, update = false) => {
    const firstName = user.user.firstName ?? "";
    const lastName = user.user.lastName ?? "";
    const fullName = `${firstName} ${lastName}`.trim();
    const email = user.user.email;
    const mentionDisplay = fullName || email; // Prefer full name, fall back to email if name is empty

    const formik = update ? updateCommentFormik : commentFormik;
    const textareaRefCurrent = update
      ? textareaRefUpdate.current
      : textareaRef.current;

    const currentText = formik.values.commentText;
    const cursorPosition =
      textareaRefCurrent?.selectionStart || currentText.length;

    const textBeforeCursor = currentText.substring(0, cursorPosition);
    const lastAt = textBeforeCursor.lastIndexOf("@");

    if (lastAt === -1) {
      setIsMentioning(false);
      setFilteredUsers([]);
      return;
    }

    // Determine the query text that needs to be replaced
    const textAfterAt = currentText.substring(lastAt + 1);
    const queryEndIndex = textAfterAt.indexOf(" "); // Find the first space after '@'
    const queryText =
      queryEndIndex !== -1
        ? textAfterAt.substring(0, queryEndIndex)
        : textAfterAt;

    // Construct the new text by replacing '@query' with '@mentionDisplay '
    const newText =
      currentText.substring(0, lastAt) + // Part before the '@'
      "@" +
      mentionDisplay +
      " " + // The new mention + a space
      currentText.substring(lastAt + 1 + queryText.length); // Part after the original query

    formik.setFieldValue("commentText", newText);

    // Manually set cursor position after insertion
    const newCursorPosition = lastAt + 1 + mentionDisplay.length + 1; // After "@<name> "
    setTimeout(() => {
      if (textareaRefCurrent) {
        textareaRefCurrent.selectionStart = newCursorPosition;
        textareaRefCurrent.selectionEnd = newCursorPosition;
      }
    }, 0);


    const resolvedEmails = resolveMentionsToEmails(newText);
    formik.setFieldValue("mantionEmail", resolvedEmails);

    // Reset mention state
    setIsMentioning(false);
    setFilteredUsers([]);
  };

  const renderHighlightedText = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    const mentionRegex = /@(\S+)/g; // Match @something without space

    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const preText = text.substring(lastIndex, match.index);
      if (preText) parts.push(preText);

      const mentionStart = match.index;
      const mentionEnd = mentionRegex.lastIndex;

      let bestMatchUser = null;
      let bestMatchLength = 0;
      let matchingText = "";

      if (projectDetailQuery.data?.data.data.assignedUsers) {
        for (const assignedUser of projectDetailQuery.data.data.data
          .assignedUsers) {
          const fullName = `${assignedUser.user.firstName ?? ""} ${
            assignedUser.user.lastName ?? ""
          }`.trim();
          const email = assignedUser.user.email || "";

          const possibleMentions = [fullName, email];

          for (const possible of possibleMentions) {
            if (!possible) continue;

            // Check if text starting after @ matches this possible user
            const substr = text.substring(
              mentionStart + 1,
              mentionStart + 1 + possible.length
            );

            if (
              substr.toLowerCase() === possible.toLowerCase() &&
              possible.length > bestMatchLength
            ) {
              bestMatchUser = assignedUser;
              bestMatchLength = possible.length;
              matchingText = substr;
            }
          }
        }
      }

      if (bestMatchUser) {
        // Push the highlight
        parts.push(
          <span
            key={`${match.index}-highlight`}
            className="text-primary-700 font-semibold"
          >
            @{matchingText}
          </span>
        );
        lastIndex = mentionStart + 1 + bestMatchLength; // Move after the matched text
      } else {
        // No matching user found, treat as normal
        parts.push(text.substring(mentionStart, mentionEnd));
        lastIndex = mentionEnd;
      }
    }

    // Push the remaining text
    const remainingText = text.substring(lastIndex);
    if (remainingText) parts.push(remainingText);

    return parts;
  };

  const commentRender = (comment: Comments) => {
    const isEditing = commentId && commentId === comment.commentId;
    const authorName =
      comment.commentByUser.firstName && comment.commentByUser.lastName
        ? `${comment.commentByUser.firstName} ${comment.commentByUser.lastName}`
        : comment.commentByUser.email?.split("@")[0] || t("unknown");

    return (
      <div
        key={comment.commentId}
        className="group flex gap-3 items-start p-3 rounded-xl bg-slate-50/60 hover:bg-slate-50 border border-slate-100 transition-all"
      >
        <UserAvatar user={comment.commentByUser} className="w-8 h-8 rounded-full ring-2 ring-white shadow-2xs mt-0.5 flex-shrink-0" />
        <div className="flex flex-col gap-1 w-full min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-xs font-bold text-slate-800 truncate ${
                  comment.commentByUser.deletedAt ? "line-through text-rose-500" : ""
                }`}
              >
                {authorName}
              </span>
              <span className="text-[11px] text-slate-400 font-normal">
                {calculateTimeDifference(new Date(comment.createdAt), t)}
                {comment.commentByUser.deletedAt ? ` (${t("deleted_user")})` : ""}
              </span>
            </div>

            {/* Actions: Edit / Remove */}
            {allowed(comment) && !isEditing && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(comment)}
                  className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  {t("edit")}
                </button>
                <span className="text-slate-300 text-xs">·</span>
                <button
                  type="button"
                  onClick={() => setRemoveComment(comment)}
                  className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 cursor-pointer"
                >
                  {t("remove")}
                </button>
              </div>
            )}
          </div>

          {isEditing ? (
            <div className="relative mt-1">
              <textarea
                ref={textareaRefUpdate}
                placeholder={t("edit_your_comment")}
                className="w-full p-2 text-xs border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 bg-white"
                name="commentText"
                onChange={handleChangeUpdate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleBlur();
                  }
                }}
                autoFocus
                value={updateCommentFormik.values.commentText}
              />
              {isMentioning &&
                filteredUsers &&
                filteredUsers.length > 0 &&
                commentId === comment.commentId && (
                  <div className="absolute z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-72 max-h-40 overflow-y-auto top-full mt-1 p-1">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.user.userId}
                        className="flex items-center gap-2 p-1.5 hover:bg-primary-50 rounded-lg cursor-pointer text-xs"
                        onClick={() => handleSelectUser(user, true)}
                      >
                        <UserAvatar user={user.user} className="w-5 h-5 text-[9px]" />
                        <span>
                          {user.user.firstName} {user.user.lastName || user.user.email}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              {updateCommentFormik.errors.commentText && (
                <ErrorMessage>
                  {t(updateCommentFormik.errors.commentText)}
                </ErrorMessage>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  type="button"
                  variant="none"
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                  onClick={() => setCommentId(null)}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="button"
                  variant="none"
                  isLoading={updateTaskCommentMutation.isPending}
                  disabled={updateTaskCommentMutation.isPending}
                  className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-2xs transition-colors cursor-pointer"
                  onClick={() => updateCommentFormik.submitForm()}
                >
                  {t("save")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-700 leading-relaxed break-words whitespace-pre-wrap">
              {renderHighlightedText(comment.commentText)}
              {comment.createdAt !== comment.updatedAt && (
                <span className="text-[10px] text-slate-400 italic ml-1">
                  ({t("edited")})
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (textareaRef.current && commentFormik.values.commentText) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    } else if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [commentFormik.values.commentText]);

  useEffect(() => {
    if (textareaRefUpdate.current && updateCommentFormik.values.commentText) {
      textareaRefUpdate.current.style.height = "auto";
      textareaRefUpdate.current.style.height =
        textareaRefUpdate.current.scrollHeight + "px";
    } else if (textareaRefUpdate.current) {
      textareaRefUpdate.current.style.height = "auto";
    }
  }, [updateCommentFormik.values.commentText]);

  return (
    <>
      <div className="w-full flex flex-col gap-4">
        {/* Comment Input Composer (Top) */}
        {props.allowed && (
          <div className="flex gap-3 items-start bg-slate-50/80 p-3 rounded-xl border border-slate-200">
            <UserAvatar user={currantUser.user} className="w-8 h-8 rounded-full ring-2 ring-white shadow-2xs mt-0.5 flex-shrink-0" />
            <div className="relative w-full flex-1">
              <div className="relative rounded-xl border border-slate-200 bg-white focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 transition-all shadow-2xs">
                <textarea
                  ref={textareaRef}
                  placeholder={t("write_a_comment")}
                  className="w-full bg-transparent p-3 pr-12 text-xs text-slate-800 placeholder-slate-400 border-none outline-none focus:outline-none focus:ring-0 ring-0 resize-none min-h-[48px]"
                  name="commentText"
                  onChange={handleChange}
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      commentFormik.handleSubmit();
                    }
                  }}
                  value={commentFormik.values.commentText}
                />
                <Button
                  type="button"
                  variant="none"
                  size="icon"
                  disabled={!commentFormik.values.commentText.trim() || createTaskCommentMutation.isPending}
                  className={`absolute bottom-2 right-2 w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                    commentFormik.values.commentText.trim()
                      ? "bg-blue-600 text-white shadow-2xs hover:bg-blue-700 cursor-pointer"
                      : "bg-slate-100 text-slate-300 cursor-not-allowed opacity-50"
                  }`}
                  onClick={commentFormik.submitForm}
                >
                  <img
                    src={Send}
                    className={`w-3.5 h-3.5 transition-all ${
                      commentFormik.values.commentText.trim() ? "brightness-0 invert" : "grayscale opacity-30"
                    }`}
                    alt="Send"
                  />
                </Button>
              </div>

              {/* Mention suggestions dropdown */}
              {isMentioning && filteredUsers && filteredUsers.length > 0 && !commentId && (
                <div className="absolute z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-72 max-h-48 overflow-y-auto top-full mt-1.5 p-1">
                  <div className="px-2.5 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    {t("mention_teammate")}
                  </div>
                  {filteredUsers.map((u) => (
                    <div
                      key={u.user.userId}
                      className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-primary-50 cursor-pointer transition-colors"
                      onClick={() => handleSelectUser(u)}
                    >
                      <UserAvatar user={u.user} className="w-6 h-6 text-[10px] rounded-full" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-semibold text-slate-800 truncate">
                          {u.user.firstName} {u.user.lastName}
                        </span>
                        <span className="text-[10px] text-slate-400 truncate">
                          {u.user.email}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {commentFormik.errors.commentText && (
          <ErrorMessage>{t(commentFormik.errors.commentText)}</ErrorMessage>
        )}

        {/* Chronological comments list */}
        <div
          className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-1"
          ref={scrollDemoRef}
        >
          {visibleComments.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400">
              {t("no_comments_yet")}
            </div>
          ) : (
            visibleComments.map((comment) => {
              return commentRender(comment);
            })
          )}

          {props.task?.comments?.length !== visibleComments?.length &&
            Boolean(props.task?.comments?.length) && (
              <div className="pt-2 text-center">
                <button
                  type="button"
                  className="text-xs text-primary-600 hover:text-primary-700 font-semibold cursor-pointer px-3 py-1 rounded-md hover:bg-primary-50 transition-colors"
                  onClick={() => setShowAllShow((prev) => !prev)}
                >
                  {!showAllShow ? t("show_older_comments") : t("show_less")}
                </button>
              </div>
            )}
        </div>
      </div>
      <Dialog
        isOpen={Boolean(removeCommnet)}
        onClose={() => setRemoveComment(undefined)}
        modalClass="rounded-xl max-w-sm p-5"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <img src={TrashCan} className="w-12 h-12" alt="Trash" />
          <h3 className="font-bold text-slate-800 text-sm">
            {t("delete_comment_confirm_msg")}
          </h3>
          <div className="flex items-center gap-2 mt-2 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRemoveComment(undefined)}
              isLoading={removeTaskCommentMutation.isPending}
              disabled={removeTaskCommentMutation.isPending}
              className="flex-1 text-xs"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (removeCommnet) handleRemove(removeCommnet);
              }}
              isLoading={removeTaskCommentMutation.isPending}
              className="flex-1 text-xs"
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default TaskComment;
